import { useMemo, useState } from "react";
import useAxios from "axios-hooks";
import {
  CommandBarButton,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  IconButton,
  Link,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  Text,
  TextField
} from "@fluentui/react";
import MessageBarComponent from "../../../Util/MessageBarComponent";
import DetailsTable from "../../../Table/DetailsTable";
import AddCertificateDialog from "./AddCertificateDialog";
import DeleteCertificateDialog from "./DeleteCertificateDialog";
import env from "../../../../env";

const { REACT_APP_STS_BASE_URL } = env;

const statusColorMap = {
  Expired: "#a4262c",
  ExpiringIn7Days: "#a4262c",
  ExpiringIn30Days: "#8a6f00",
  ExpiringIn90Days: "#876400",
  ExpiringInMoreThan90Days: "#107c10",
  InvalidCertificate: "#a4262c"
};

const formatStatus = (status) => {
  if (!status) {
    return "Unknown";
  }

  switch (status) {
    case "ExpiringIn7Days":
      return "Expiring in 7 days";
    case "ExpiringIn30Days":
      return "Expiring in 30 days";
    case "ExpiringIn90Days":
      return "Expiring in 90 days";
    case "ExpiringInMoreThan90Days":
      return "Valid (90+ days)";
    case "InvalidCertificate":
      return "Invalid certificate";
    default:
      return status;
  }
};

const formatUtcDate = (dateValue) => {
  if (!dateValue || dateValue === "0001-01-01T00:00:00") {
    return "N/A";
  }

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()} UTC`;
};

const BooleanValue = ({ value }) => <span>{value ? "Yes" : "No"}</span>;

const buildSuggestedSsoUrl = (entityId) => {
  if (!REACT_APP_STS_BASE_URL || !entityId) {
    return "";
  }

  const baseUrl = REACT_APP_STS_BASE_URL.endsWith("/")
    ? REACT_APP_STS_BASE_URL
    : `${REACT_APP_STS_BASE_URL}/`;

  return `${baseUrl}SAML/InitiateSingleSignOn?idpEntityId=${encodeURIComponent(entityId)}`;
};

const moreIcon = { iconName: "More" };

const getApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;
  const errors = responseData?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    const firstError = errors[0];

    if (typeof firstError === "string") {
      return firstError;
    }

    if (firstError?.errorMessage) {
      return firstError.errorMessage;
    }
  }

  if (typeof responseData === "string") {
    return responseData;
  }

  if (responseData?.message) {
    return responseData.message;
  }

  return fallbackMessage;
};

const CobrandIdpCard = ({ cobrandIdp, selectedCobrandId, onCertificateAdded }) => {
  const [copyFeedback, setCopyFeedback] = useState("");
  const [copyFeedbackType, setCopyFeedbackType] = useState(MessageBarType.success);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState(null);
  const [cardFeedback, setCardFeedback] = useState("");
  const [cardFeedbackType, setCardFeedbackType] = useState(MessageBarType.success);
  const [showDeleteIdpDialog, setShowDeleteIdpDialog] = useState(false);
  const [deleteIdpDialogError, setDeleteIdpDialogError] = useState("");
  const [deleteIdpConfirmationText, setDeleteIdpConfirmationText] = useState("");
  const [{ loading: deletingIdp }, deleteCobrandIdp] = useAxios(
    { url: "/internal/cobrand/0/delete-cobrandidp", method: "POST" },
    { manual: true }
  );
  const certificates = cobrandIdp?.certificates || [];
  const entityId = cobrandIdp?.entityId || "Unknown Entity Id";
  const suggestedSsoUrl = buildSuggestedSsoUrl(entityId);
  const certificateColumns = useMemo(
    () => [
      {
        key: "use",
        name: "Use",
        fieldName: "use",
        minWidth: 90,
        maxWidth: 110,
        isResizable: true
      },
      {
        key: "status",
        name: "Status",
        fieldName: "status",
        minWidth: 140,
        maxWidth: 180,
        isResizable: true,
        onRender: (item) => (
          <span
            style={{
              color: statusColorMap[item.status] || "#323130",
              fontWeight: 600
            }}
          >
            {formatStatus(item.status)}
          </span>
        )
      },
      {
        key: "expirationDateUtc",
        name: "Expires (UTC)",
        fieldName: "expirationDateUtc",
        minWidth: 180,
        maxWidth: 220,
        isResizable: true,
        onRender: (item) => formatUtcDate(item.expirationDateUtc)
      },
      {
        key: "thumbprint",
        name: "Thumbprint",
        fieldName: "thumbprint",
        minWidth: 220,
        maxWidth: 320,
        isResizable: true
      },
      {
        key: "subject",
        name: "Subject",
        fieldName: "subject",
        minWidth: 260,
        maxWidth: 420,
        isResizable: true,
        onRender: (item) => (
          <span style={{ wordBreak: "break-word" }}>{item.subject || "N/A"}</span>
        )
      },
      {
        key: "menu",
        minWidth: 30,
        maxWidth: 30,
        onRender: (item) => (
          <IconButton
            iconProps={moreIcon}
            menuProps={{
              items: [
                {
                  key: "delete",
                  iconProps: { iconName: "Delete" },
                  text: "Delete",
                  onClick: () => {
                    setCertificateToDelete(item);
                    setShowDeleteDialog(true);
                  }
                }
              ]
            }}
            onRenderMenuIcon={() => {}}
          />
        )
      }
    ],
    []
  );

  const handleCopySuggestedSsoUrl = async () => {
    try {
      if (!suggestedSsoUrl) {
        throw new Error("Suggested SSO URL unavailable");
      }

      if (!navigator.clipboard || !navigator.clipboard.writeText) {
        throw new Error("Clipboard API unavailable");
      }

      await navigator.clipboard.writeText(suggestedSsoUrl);
      setCopyFeedback("Suggested SSO URL copied to clipboard.");
      setCopyFeedbackType(MessageBarType.success);
      setTimeout(() => setCopyFeedback(""), 3000);
    } catch (error) {
      setCopyFeedback("Unable to copy URL. Please copy it manually.");
      setCopyFeedbackType(MessageBarType.error);
    }
  };

  const openDeleteIdpDialog = () => {
    setDeleteIdpDialogError("");
    setDeleteIdpConfirmationText("");
    setShowDeleteIdpDialog(true);
  };

  const closeDeleteIdpDialog = () => {
    setDeleteIdpDialogError("");
    setDeleteIdpConfirmationText("");
    setShowDeleteIdpDialog(false);
  };

  const handleDeleteIdp = async () => {
    setDeleteIdpDialogError("");

    if (!selectedCobrandId || !cobrandIdp?.cobrandIdpId) {
      setDeleteIdpDialogError("Unable to delete IdP. Reload and try again.");
      return;
    }

    if (deleteIdpConfirmationText.trim().toLowerCase() !== (entityId || "").trim().toLowerCase()) {
      setDeleteIdpDialogError("Type the exact Entity Id to confirm this delete action.");
      return;
    }

    try {
      await deleteCobrandIdp({
        url: `/internal/cobrand/${selectedCobrandId}/delete-cobrandidp`,
        data: {
          cobrandIdpId: cobrandIdp.cobrandIdpId,
          entityIdConfirmationText: deleteIdpConfirmationText
        }
      });

      setCardFeedback("IdP deleted successfully.");
      setCardFeedbackType(MessageBarType.success);
      closeDeleteIdpDialog();

      if (onCertificateAdded) {
        await onCertificateAdded();
      }
    } catch (error) {
      setDeleteIdpDialogError(getApiErrorMessage(error, "Unable to delete IdP."));
    }
  };

  return (
    <div className="panel panel-default mt20" style={{ border: "1px solid #dddddd", padding: 8 }}>
      <div
        className="panel-heading panel-heading-em"
        style={{ backgroundColor: "#f5f5f5", border: "1px solid #e5e5e5", marginBottom: 8 }}
      >
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <Text variant="small"><b>Entity Id:</b> {entityId}</Text>
          <CommandBarButton
            text="Delete IdP"
            iconProps={{ iconName: "Delete" }}
            onClick={openDeleteIdpDialog}
            styles={{
              root: {
                minHeight: 32,
                paddingLeft: 12,
                paddingRight: 12
              },
              label: {
                fontSize: 14
              }
            }}
          />
        </Stack>
      </div>
      <div className="panel-body" style={{ padding: 12 }}>
        <Stack tokens={{ childrenGap: 6 }}>
          {cardFeedback && (
            <MessageBar
              messageBarType={cardFeedbackType}
              onDismiss={() => setCardFeedback("")}
              isMultiline={false}
            >
              {cardFeedback}
            </MessageBar>
          )}

          <Text variant="small"><b>Suggested SSO Url:</b></Text>
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" style={{ maxWidth: 860 }}>
            <Link
              href={suggestedSsoUrl || undefined}
              target="_blank"
              rel="noopener noreferrer"
              title={suggestedSsoUrl || "Set REACT_APP_STS_BASE_URL to enable this link"}
              disabled={!suggestedSsoUrl}
            >
              {suggestedSsoUrl || "Unavailable"}
            </Link>
            <IconButton
              iconProps={{ iconName: "Copy" }}
              title="Copy suggested SSO URL"
              ariaLabel="Copy suggested SSO URL"
              onClick={handleCopySuggestedSsoUrl}
              disabled={!suggestedSsoUrl}
            />
            {copyFeedback && (
              <div style={{ display: "inline-block", whiteSpace: "nowrap" }}>
                <MessageBarComponent
                  messageType={copyFeedbackType}
                  message={copyFeedback}
                  showMessage={Boolean(copyFeedback)}
                  setShowMessage={() => setCopyFeedback("")}
                  isMultiline={false}
                />
              </div>
            )}
          </Stack>

          <Text variant="small"><b>SSO Binding:</b> {cobrandIdp?.singleSignOnServiceBinding || "N/A"}</Text>
          <Text variant="small"><b>SSO Binding URL:</b> {cobrandIdp?.singleSignOnServiceUrl || "N/A"}</Text>
          <Text variant="small"><b>SLO Binding:</b> {cobrandIdp?.singleLogoutServiceBinding || "N/A"}</Text>
          <Text variant="small"><b>SLO Binding URL:</b> {cobrandIdp?.singleLogoutServiceUrl || "N/A"}</Text>
          <Text variant="small"><b>Sign Authn Request:</b> <BooleanValue value={cobrandIdp?.signAuthnRequest} /></Text>
          <Text variant="small"><b>Sign Logout Request:</b> <BooleanValue value={cobrandIdp?.signLogoutRequest} /></Text>
          <Text variant="small"><b>Sign Logout Response:</b> <BooleanValue value={cobrandIdp?.signLogoutResponse} /></Text>
          <Text variant="small"><b>Want Logout Request Signed:</b> <BooleanValue value={cobrandIdp?.wantLogoutRequestSigned} /></Text>
          <Text variant="small"><b>Want Logout Response Signed:</b> <BooleanValue value={cobrandIdp?.wantLogoutResponseSigned} /></Text>
        </Stack>

        <div className="mt20">
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
            <Text variant="mediumPlus">Certificates</Text>
            <DefaultButton text="Add" onClick={() => setShowAddDialog(true)} iconProps={{ iconName: "Add" }} />
          </Stack>

          {certificates.length === 0 && (
            <Text variant="small" style={{ display: "block", marginTop: 8 }}>
              No certificates found for this Cobrand IdP.
            </Text>
          )}

          {certificates.length > 0 && (
            <DetailsTable
              items={certificates}
              columns={certificateColumns}
              hideSelectionDetails
              hideFilterField
              shouldUsePagination={false}
              itemsPerPage={25}
              selectionMode="none"
              style={{ width: "100%", marginTop: 10 }}
            />
          )}
        </div>

        <AddCertificateDialog
          hidden={!showAddDialog}
          selectedCobrandId={selectedCobrandId}
          cobrandIdpId={cobrandIdp?.cobrandIdpId}
          onDismiss={() => setShowAddDialog(false)}
          onAdded={async () => {
            setCardFeedback("Certificate added successfully.");
            setCardFeedbackType(MessageBarType.success);
            if (onCertificateAdded) {
              await onCertificateAdded();
            }
          }}
        />

        <DeleteCertificateDialog
          hidden={!showDeleteDialog}
          certificate={certificateToDelete}
          selectedCobrandId={selectedCobrandId}
          cobrandIdpId={cobrandIdp?.cobrandIdpId}
          entityId={entityId}
          certificates={certificates}
          onDismiss={() => {
            setShowDeleteDialog(false);
            setCertificateToDelete(null);
          }}
          onDeleted={async () => {
            setCardFeedback("Certificate deleted successfully.");
            setCardFeedbackType(MessageBarType.success);
            if (onCertificateAdded) {
              await onCertificateAdded();
            }
          }}
        />

        <Dialog
          hidden={!showDeleteIdpDialog}
          onDismiss={closeDeleteIdpDialog}
          dialogContentProps={{
            type: DialogType.normal,
            title: "Delete IdP",
            subText: "This action removes the Cobrand IdP record and all related certificates."
          }}
          minWidth={520}
        >
          <Stack tokens={{ childrenGap: 10 }}>
            {deleteIdpDialogError && (
              <MessageBar messageBarType={MessageBarType.error} isMultiline>
                {deleteIdpDialogError}
              </MessageBar>
            )}

            <Text variant="small">Type the exact Entity Id to confirm deletion.</Text>
            <TextField
              label={`Entity Id: ${entityId}`}
              value={deleteIdpConfirmationText}
              onChange={(_, value) => setDeleteIdpConfirmationText(value || "")}
              autoComplete="off"
              errorMessage={
                deleteIdpConfirmationText
                && deleteIdpConfirmationText.trim().toLowerCase() !== (entityId || "").trim().toLowerCase()
                  ? "Entity Id does not match."
                  : ""
              }
            />
          </Stack>

          <DialogFooter>
            <PrimaryButton
              text={deletingIdp ? "Deleting..." : "Delete IdP"}
              onClick={handleDeleteIdp}
              disabled={deletingIdp}
            />
            <DefaultButton text="Cancel" onClick={closeDeleteIdpDialog} disabled={deletingIdp} />
          </DialogFooter>
        </Dialog>
      </div>
    </div>
  );
};

export default CobrandIdpCard;
